import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import StatusBadge from '../../components/common/StatusBadge';
import { SkeletonTable } from '../../components/common/Skeleton';
import CopyButton from '../../components/common/CopyButton';
import { 
  ShieldCheck, 
  Sliders, 
  RefreshCw, 
  DollarSign, 
  User, 
  Zap, 
  AlertCircle, 
  Eye, 
  FileText, 
  Download, 
  Search, 
  Terminal, 
  CheckCircle2, 
  Copy 
} from 'lucide-react';

const ProfitSharingSettings = () => {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ totalSystemClosedPnL: 0, totalSystemProfitShared: 0, totalRecords: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState({ type: '', text: '' });

  // Inspection modal state for Payment Flow Breakdown
  const [selectedRecordForFlow, setSelectedRecordForFlow] = useState(null);

  // Tier-wise Commission & Sync Settings State
  const [commConfig, setCommConfig] = useState({
    adminOverrideRate: 20,
    activationFeeAmount: 100,
    activationFeeOption1: 100,
    activationFeeOption2: 60,
    activationAdminSplitOption1: 80,
    activationReferrerSplitOption1: 20,
    activationAdminSplitOption2: 80,
    activationReferrerSplitOption2: 20,
    pnlSyncIntervalMinutes: 10080,
    referralRates: { level1: 4, level2: 3, level3: 2, level4: 1, level5: 2 }
  });
  const [savingCommConfig, setSavingCommConfig] = useState(false);

  // Manual deduction modal
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [manualUserId, setManualUserId] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [manualNotes, setManualNotes] = useState('');
  const [processingDeduction, setProcessingDeduction] = useState(false);

  // Audit History Modal State
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);
  const [loadingLogDetail, setLoadingLogDetail] = useState(false);
  const [logSearchTerm, setLogSearchTerm] = useState('');
  const [logCopied, setLogCopied] = useState(false);

  const fetchAdminProfitData = async () => {
    try {
      const res = await axiosClient.get('/profit/admin/records');
      setRecords(res.data.records || []);
      setSummary(res.data.summary || { totalSystemClosedPnL: 0, totalSystemProfitShared: 0, totalRecords: 0 });

      const resComm = await axiosClient.get('/admin/commission-settings');
      if (resComm.data && resComm.data.config) {
        setCommConfig(resComm.data.config);
      }
    } catch (err) {
      console.error('[Admin Profit Fetch Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminProfitData();
  }, []);

  const handleSaveCommissionSettings = async (e) => {
    e.preventDefault();
    setSavingCommConfig(true);
    setFeedbackMsg({ type: '', text: '' });
    try {
      const res = await axiosClient.put('/admin/commission-settings', commConfig);
      setFeedbackMsg({ type: 'success', text: res.data.message || 'Commission settings updated!' });
      if (res.data.config) {
        setCommConfig(res.data.config);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update commission permissions.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setSavingCommConfig(false);
    }
  };

  const handleTriggerSyncNow = async () => {
    setSyncing(true);
    setFeedbackMsg({ type: '', text: '' });
    try {
      const res = await axiosClient.post('/profit/admin/sync-now', {});
      setFeedbackMsg({ type: 'success', text: res.data.message || 'Instant PnL Sync executed successfully!' });
      fetchAdminProfitData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to execute manual sync.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setSyncing(false);
    }
  };

  const handleManualDeductionSubmit = async (e) => {
    e.preventDefault();
    if (!manualUserId || !manualAmount) return;
    setProcessingDeduction(true);
    setFeedbackMsg({ type: '', text: '' });

    try {
      const res = await axiosClient.post('/profit/admin/manual-deduction', {
        userId: manualUserId,
        deductionAmount: manualAmount,
        notes: manualNotes
      });
      setFeedbackMsg({ type: 'success', text: res.data.message || 'Manual profit deduction processed!' });
      setShowDeductionModal(false);
      setManualUserId('');
      setManualAmount('');
      setManualNotes('');
      fetchAdminProfitData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to process manual deduction.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setProcessingDeduction(false);
    }
  };

  // Open Audit History Modal and fetch past cycle log list
  const handleOpenAuditModal = async () => {
    setShowAuditModal(true);
    setLoadingAuditLogs(true);
    setSelectedLogDetail(null);
    try {
      const res = await axiosClient.get('/profit/admin/audit-logs');
      const logs = res.data.logs || [];
      setAuditLogs(logs);
      if (logs.length > 0) {
        handleSelectLog(logs[0]._id || logs[0].cycleId);
      }
    } catch (err) {
      console.error('[Fetch Audit Logs Error]:', err);
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  const handleSelectLog = async (logId) => {
    setLoadingLogDetail(true);
    try {
      const res = await axiosClient.get(`/profit/admin/audit-logs/${logId}`);
      setSelectedLogDetail(res.data.log);
    } catch (err) {
      console.error('[Fetch Audit Log Detail Error]:', err);
    } finally {
      setLoadingLogDetail(false);
    }
  };

  const handleDownloadLogFile = (logId, cycleId) => {
    window.open(`${axiosClient.defaults.baseURL}/profit/admin/audit-logs/${logId}/download`, '_blank');
  };

  const handleCopyLogText = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setLogCopied(true);
    setTimeout(() => setLogCopied(false), 2000);
  };

  // Filter terminal lines based on logSearchTerm
  const getFilteredLogLines = () => {
    if (!selectedLogDetail || !selectedLogDetail.logContentText) return [];
    const allLines = selectedLogDetail.logContentText.split('\n');
    if (!logSearchTerm.trim()) return allLines;
    const term = logSearchTerm.toLowerCase();
    return allLines.filter(line => line.toLowerCase().includes(term));
  };

  return (
    <div className="space-y-6">
      {/* Admin Title & Sync Action Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-[#121215] p-5 rounded-2xl border border-[#27272A] shadow-card gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <ShieldCheck className="w-5 h-5 text-[#F97316]" />
            <h2 className="text-lg font-extrabold text-[#FFFFFF]">Profit Sharing & PnL Sync Control</h2>
          </div>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Automated PnL synchronization engine processes closed trades and calculates profit sharing distributions.
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <Button 
            variant="secondary" 
            size="sm"
            onClick={handleOpenAuditModal}
            className="flex items-center space-x-2 border-[#3B82F6]/40 text-[#60A5FA] hover:bg-[#3B82F6]/10"
          >
            <FileText className="w-4 h-4 text-[#60A5FA]" />
            <span>Audit History</span>
          </Button>

          <Button 
            variant="secondary" 
            size="sm"
            onClick={() => setShowDeductionModal(true)}
          >
            Manual Adjustment
          </Button>
          <Button 
            variant="primary" 
            size="sm" 
            onClick={handleTriggerSyncNow}
            disabled={syncing}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            <span>{syncing ? 'Syncing...' : 'Run PnL Sync Now'}</span>
          </Button>
        </div>
      </div>

      {feedbackMsg.text && (
        <div className={`p-4 rounded-2xl border text-xs font-semibold ${
          feedbackMsg.type === 'success' 
            ? 'bg-[#10B981]/15 border-[#10B981]/30 text-[#10B981]' 
            : 'bg-[#EF4444]/15 border-[#EF4444]/30 text-[#EF4444]'
        }`}>
          {feedbackMsg.text}
        </div>
      )}

      {/* Metric & Sync Schedule Overview (4 Cards in 1 Row) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card hoverEffect>
          <span className="text-xs text-[#9CA3AF] font-semibold">Total System Closed PnL</span>
          <p className="text-2xl font-extrabold text-[#10B981] mt-2">+${summary.totalSystemClosedPnL.toFixed(2)}</p>
          <p className="text-[10px] text-[#9CA3AF] mt-1">Across all connected MT5 accounts</p>
        </Card>

        <Card hoverEffect>
          <span className="text-xs text-[#9CA3AF] font-semibold">Total Profit Share Collected</span>
          <p className="text-2xl font-extrabold text-[#F97316] mt-2">${summary.totalSystemProfitShared.toFixed(2)}</p>
          <p className="text-[10px] text-[#9CA3AF] mt-1">Platform revenue share</p>
        </Card>

        <Card hoverEffect>
          <span className="text-xs text-[#9CA3AF] font-semibold">Total Sync Cycles Logged</span>
          <p className="text-2xl font-extrabold text-[#FFFFFF] mt-2">{summary.totalRecords} Cycles</p>
          <p className="text-[10px] text-[#10B981] mt-1">Automated background worker active</p>
        </Card>

        <Card hoverEffect className="border-[#10B981]/40">
          <span className="text-xs text-[#10B981] font-bold">Automated PnL Sync Cycle</span>
          <div className="mt-2">
            <select
              value={commConfig.pnlSyncIntervalMinutes ?? 10080}
              onChange={(e) => setCommConfig({ ...commConfig, pnlSyncIntervalMinutes: Number(e.target.value) })}
              className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] font-mono text-xs font-bold rounded-xl p-2 focus:outline-none focus:border-[#10B981]"
            >
              <option value={10080}>1 Week (7 Days) [Default]</option>
              <option value={20160}>2 Weeks (14 Days)</option>
              <option value={43200}>1 Month (30 Days)</option>
              <option value={1440}>1 Day (24 Hours)</option>
              <option value={720}>12 Hours</option>
              <option value={60}>1 Hour</option>
            </select>
          </div>
          <p className="text-[10px] text-[#9CA3AF] mt-1">MT5 balance comparison schedule</p>
        </Card>
      </div>

      {/* Tier-Wise Commission & Profit Share Control Panel */}
      <form onSubmit={handleSaveCommissionSettings} className="space-y-6">
        {/* SECTION 1: ACTIVATION FEE OPTIONS & SPLIT CUTS */}
        <Card 
          title="⚡ Platform Activation Fee Options & Split Distribution" 
          subtitle="Configure the two activation fee price tiers and their respective Admin & Referrer cut percentages"
          className="border-[#F59E0B]/30"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Option 1 Settings Sub-Card */}
            <div className="bg-[#09090B] p-5 rounded-2xl border border-[#F59E0B]/30 flex flex-col justify-between space-y-4 shadow-inner">
              <div className="space-y-4">
                {/* Option 1 Header */}
                <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
                  <div className="flex items-center space-x-2.5">
                    <div className="p-2 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/30">
                      <Zap className="w-4 h-4 text-[#F59E0B] fill-current" />
                    </div>
                    <div>
                      <h4 className="text-xs font-extrabold text-[#FFFFFF] uppercase tracking-wider">Option 1 Activation Tier</h4>
                      <p className="text-[10px] text-[#9CA3AF]">First tier activation fee charged to new users</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-[#F59E0B] font-mono font-bold bg-[#F59E0B]/10 px-2.5 py-1 rounded-full border border-[#F59E0B]/30 shrink-0">
                    ${commConfig.activationFeeOption1 || 100} Tier
                  </span>
                </div>

                {/* Option 1 Input Fields */}
                <div className="space-y-4">
                  {/* Fee Input */}
                  <div>
                    <label className="block text-xs font-bold text-[#F59E0B] mb-1.5">
                      Activation Fee ($ USD)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-[#9CA3AF] font-bold text-xs font-mono">
                        $
                      </div>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        required
                        value={commConfig.activationFeeOption1 ?? 100}
                        onChange={(e) => setCommConfig({ ...commConfig, activationFeeOption1: e.target.value })}
                        className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] font-mono text-sm font-bold rounded-xl pl-8 pr-3 py-2.5 focus:outline-none focus:border-[#F59E0B] transition-colors"
                        placeholder="100"
                      />
                    </div>
                    <p className="text-[10px] text-[#9CA3AF] mt-1">User pays this price to activate account via Option 1</p>
                  </div>

                  {/* Cuts Row (Side by Side) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                    {/* Admin Cut Option 1 % */}
                    <div>
                      <label className="block text-xs font-bold text-[#10B981] mb-1.5">
                        Admin Cut (%)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          required
                          value={commConfig.activationAdminSplitOption1 ?? 80}
                          onChange={(e) => setCommConfig({ ...commConfig, activationAdminSplitOption1: e.target.value })}
                          className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] font-mono text-sm font-bold rounded-xl pr-8 pl-3.5 py-2.5 focus:outline-none focus:border-[#10B981] transition-colors"
                          placeholder="80"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none text-[#10B981] font-bold text-xs font-mono">
                          %
                        </div>
                      </div>
                      <p className="text-[10px] text-[#9CA3AF] mt-1">Credited to Admin wallet</p>
                    </div>

                    {/* Direct Referrer Cut Option 1 % */}
                    <div>
                      <label className="block text-xs font-bold text-[#3B82F6] mb-1.5">
                        Direct Referrer Cut (%)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          required
                          value={commConfig.activationReferrerSplitOption1 ?? 20}
                          onChange={(e) => setCommConfig({ ...commConfig, activationReferrerSplitOption1: e.target.value })}
                          className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] font-mono text-sm font-bold rounded-xl pr-8 pl-3.5 py-2.5 focus:outline-none focus:border-[#3B82F6] transition-colors"
                          placeholder="20"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none text-[#3B82F6] font-bold text-xs font-mono">
                          %
                        </div>
                      </div>
                      <p className="text-[10px] text-[#9CA3AF] mt-1">Credited to Level-1 referrer</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Live Payout Breakdown Preview */}
              <div className="mt-4 bg-[#121215] p-3 rounded-xl border border-[#27272A] flex flex-wrap items-center justify-between gap-2 text-xs font-mono">
                <span className="text-[10px] text-[#9CA3AF] font-sans font-semibold">Live Payout Split:</span>
                <div className="flex items-center space-x-2 text-[11px]">
                  <span className="text-[#10B981] font-bold">
                    Admin: ${((Number(commConfig.activationFeeOption1 || 0) * Number(commConfig.activationAdminSplitOption1 || 0)) / 100).toFixed(2)} ({commConfig.activationAdminSplitOption1 || 0}%)
                  </span>
                  <span className="text-[#27272A]">|</span>
                  <span className="text-[#3B82F6] font-bold">
                    Referrer: ${((Number(commConfig.activationFeeOption1 || 0) * Number(commConfig.activationReferrerSplitOption1 || 0)) / 100).toFixed(2)} ({commConfig.activationReferrerSplitOption1 || 0}%)
                  </span>
                </div>
              </div>
            </div>

            {/* Option 2 Settings Sub-Card */}
            <div className="bg-[#09090B] p-5 rounded-2xl border border-[#F97316]/30 flex flex-col justify-between space-y-4 shadow-inner">
              <div className="space-y-4">
                {/* Option 2 Header */}
                <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
                  <div className="flex items-center space-x-2.5">
                    <div className="p-2 rounded-xl bg-[#F97316]/10 border border-[#F97316]/30">
                      <Zap className="w-4 h-4 text-[#F97316] fill-current" />
                    </div>
                    <div>
                      <h4 className="text-xs font-extrabold text-[#FFFFFF] uppercase tracking-wider">Option 2 Activation Tier</h4>
                      <p className="text-[10px] text-[#9CA3AF]">Second tier activation fee charged to new users</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-[#F97316] font-mono font-bold bg-[#F97316]/10 px-2.5 py-1 rounded-full border border-[#F97316]/30 shrink-0">
                    ${commConfig.activationFeeOption2 || 60} Tier
                  </span>
                </div>

                {/* Option 2 Input Fields */}
                <div className="space-y-4">
                  {/* Fee Input */}
                  <div>
                    <label className="block text-xs font-bold text-[#F97316] mb-1.5">
                      Activation Fee ($ USD)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-[#9CA3AF] font-bold text-xs font-mono">
                        $
                      </div>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        required
                        value={commConfig.activationFeeOption2 ?? 60}
                        onChange={(e) => setCommConfig({ ...commConfig, activationFeeOption2: e.target.value })}
                        className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] font-mono text-sm font-bold rounded-xl pl-8 pr-3 py-2.5 focus:outline-none focus:border-[#F97316] transition-colors"
                        placeholder="60"
                      />
                    </div>
                    <p className="text-[10px] text-[#9CA3AF] mt-1">User pays this price to activate account via Option 2</p>
                  </div>

                  {/* Cuts Row (Side by Side) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                    {/* Admin Cut Option 2 % */}
                    <div>
                      <label className="block text-xs font-bold text-[#10B981] mb-1.5">
                        Admin Cut (%)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          required
                          value={commConfig.activationAdminSplitOption2 ?? 80}
                          onChange={(e) => setCommConfig({ ...commConfig, activationAdminSplitOption2: e.target.value })}
                          className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] font-mono text-sm font-bold rounded-xl pr-8 pl-3.5 py-2.5 focus:outline-none focus:border-[#10B981] transition-colors"
                          placeholder="80"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none text-[#10B981] font-bold text-xs font-mono">
                          %
                        </div>
                      </div>
                      <p className="text-[10px] text-[#9CA3AF] mt-1">Credited to Admin wallet</p>
                    </div>

                    {/* Direct Referrer Cut Option 2 % */}
                    <div>
                      <label className="block text-xs font-bold text-[#3B82F6] mb-1.5">
                        Direct Referrer Cut (%)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          required
                          value={commConfig.activationReferrerSplitOption2 ?? 20}
                          onChange={(e) => setCommConfig({ ...commConfig, activationReferrerSplitOption2: e.target.value })}
                          className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] font-mono text-sm font-bold rounded-xl pr-8 pl-3.5 py-2.5 focus:outline-none focus:border-[#3B82F6] transition-colors"
                          placeholder="20"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none text-[#3B82F6] font-bold text-xs font-mono">
                          %
                        </div>
                      </div>
                      <p className="text-[10px] text-[#9CA3AF] mt-1">Credited to Level-1 referrer</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Live Payout Breakdown Preview */}
              <div className="mt-4 bg-[#121215] p-3 rounded-xl border border-[#27272A] flex flex-wrap items-center justify-between gap-2 text-xs font-mono">
                <span className="text-[10px] text-[#9CA3AF] font-sans font-semibold">Live Payout Split:</span>
                <div className="flex items-center space-x-2 text-[11px]">
                  <span className="text-[#10B981] font-bold">
                    Admin: ${((Number(commConfig.activationFeeOption2 || 0) * Number(commConfig.activationAdminSplitOption2 || 0)) / 100).toFixed(2)} ({commConfig.activationAdminSplitOption2 || 0}%)
                  </span>
                  <span className="text-[#27272A]">|</span>
                  <span className="text-[#3B82F6] font-bold">
                    Referrer: ${((Number(commConfig.activationFeeOption2 || 0) * Number(commConfig.activationReferrerSplitOption2 || 0)) / 100).toFixed(2)} ({commConfig.activationReferrerSplitOption2 || 0}%)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* SECTION 2: TRADE PROFIT SHARING & REFERRAL CASCADE RATES */}
        <Card 
          title="Master Admin Trade Cut & 5-Level Referral Cascade Rates" 
          subtitle="Configure the flat Master Admin override % and level-wise referral cascade commission rates for trade profits"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Master Admin Override Cut % */}
              <div className="bg-[#09090B] p-4 rounded-2xl border border-[#F97316]/40 space-y-2">
                <label className="block text-xs font-bold text-[#F97316]">
                  Master Admin Flat Cut (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  required
                  value={commConfig.adminOverrideRate ?? ''}
                  onChange={(e) => setCommConfig({ ...commConfig, adminOverrideRate: e.target.value })}
                  className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] font-mono text-sm font-bold rounded-xl p-2.5 focus:outline-none focus:border-[#F97316]"
                />
                <p className="text-[10px] text-[#9CA3AF]">
                  Flat percentage collected directly by Master Admin from every trade.
                </p>
              </div>

              {/* Level 1 Cascade % */}
              <div className="bg-[#09090B] p-4 rounded-2xl border border-[#27272A] space-y-2">
                <label className="block text-xs font-bold text-[#F59E0B]">
                  Level 1 Commission (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  required
                  value={commConfig.referralRates?.level1 ?? ''}
                  onChange={(e) => setCommConfig({
                    ...commConfig,
                    referralRates: { ...commConfig.referralRates, level1: e.target.value }
                  })}
                  className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] font-mono text-sm font-bold rounded-xl p-2.5 focus:outline-none focus:border-[#F59E0B]"
                />
                <p className="text-[10px] text-[#9CA3AF]">Direct referrer (1 step up) bonus.</p>
              </div>

              {/* Level 2 Cascade % */}
              <div className="bg-[#09090B] p-4 rounded-2xl border border-[#27272A] space-y-2">
                <label className="block text-xs font-bold text-[#3B82F6]">
                  Level 2 Commission (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  required
                  value={commConfig.referralRates?.level2 ?? ''}
                  onChange={(e) => setCommConfig({
                    ...commConfig,
                    referralRates: { ...commConfig.referralRates, level2: e.target.value }
                  })}
                  className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] font-mono text-sm font-bold rounded-xl p-2.5 focus:outline-none focus:border-[#3B82F6]"
                />
                <p className="text-[10px] text-[#9CA3AF]">2nd upline level bonus.</p>
              </div>

              {/* Level 3 Cascade % */}
              <div className="bg-[#09090B] p-4 rounded-2xl border border-[#27272A] space-y-2">
                <label className="block text-xs font-bold text-[#8B5CF6]">
                  Level 3 Commission (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  required
                  value={commConfig.referralRates?.level3 ?? ''}
                  onChange={(e) => setCommConfig({
                    ...commConfig,
                    referralRates: { ...commConfig.referralRates, level3: e.target.value }
                  })}
                  className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] font-mono text-sm font-bold rounded-xl p-2.5 focus:outline-none focus:border-[#8B5CF6]"
                />
                <p className="text-[10px] text-[#9CA3AF]">3rd upline level bonus.</p>
              </div>

              {/* Level 4 Cascade % */}
              <div className="bg-[#09090B] p-4 rounded-2xl border border-[#27272A] space-y-2">
                <label className="block text-xs font-bold text-[#EC4899]">
                  Level 4 Commission (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  required
                  value={commConfig.referralRates?.level4 ?? ''}
                  onChange={(e) => setCommConfig({
                    ...commConfig,
                    referralRates: { ...commConfig.referralRates, level4: e.target.value }
                  })}
                  className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] font-mono text-sm font-bold rounded-xl p-2.5 focus:outline-none focus:border-[#EC4899]"
                />
                <p className="text-[10px] text-[#9CA3AF]">4th upline level bonus.</p>
              </div>

              {/* Level 5 Cascade % */}
              <div className="bg-[#09090B] p-4 rounded-2xl border border-[#27272A] space-y-2">
                <label className="block text-xs font-bold text-[#10B981]">
                  Level 5 Commission (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  required
                  value={commConfig.referralRates?.level5 ?? ''}
                  onChange={(e) => setCommConfig({
                    ...commConfig,
                    referralRates: { ...commConfig.referralRates, level5: e.target.value }
                  })}
                  className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] font-mono text-sm font-bold rounded-xl p-2.5 focus:outline-none focus:border-[#10B981]"
                />
                <p className="text-[10px] text-[#9CA3AF]">5th upline level bonus.</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-[#27272A]">
              <div className="text-xs text-[#9CA3AF]">
                Combined Max Trade Deduction: <span className="font-mono font-bold text-[#F97316]">
                  {(
                    Number(commConfig.adminOverrideRate || 0) +
                    Number(commConfig.referralRates?.level1 || 0) +
                    Number(commConfig.referralRates?.level2 || 0) +
                    Number(commConfig.referralRates?.level3 || 0) +
                    Number(commConfig.referralRates?.level4 || 0) +
                    Number(commConfig.referralRates?.level5 || 0)
                  ).toFixed(1)}%
                </span>
              </div>
              <Button variant="primary" size="sm" type="submit" disabled={savingCommConfig}>
                {savingCommConfig ? 'Saving Permissions...' : 'Save Commission Permissions'}
              </Button>
            </div>
          </div>
        </Card>
      </form>

      {/* Admin Profit Records Table */}
      <Card title="System-Wide Profit Records" subtitle="All periodic closed PnL sync cycles and calculated deductions">
        {loading ? (
          <SkeletonTable rows={4} cols={6} />
        ) : records.length === 0 ? (
          <div className="p-8 text-center text-xs text-[#9CA3AF]">No profit records found in system.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                  <th className="py-3 px-4">Trader</th>
                  <th className="py-3 px-4">MT5 Account</th>
                  <th className="py-3 px-4">Closed PnL</th>
                  <th className="py-3 px-4">Share Rate</th>
                  <th className="py-3 px-4">Deducted</th>
                  <th className="py-3 px-4">Trader Net</th>
                  <th className="py-3 px-4">Timestamp</th>
                  <th className="py-3 px-4 text-right">Payment Flow</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A] text-xs">
                {records.map((rec) => (
                  <tr key={rec._id} className="hover:bg-[#1A1A1E] transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="flex items-center space-x-1.5">
                        <span className="font-bold text-[#FFFFFF]">{rec.userId?.name || 'User'}</span>
                        {rec.userId?.userIdTag && (
                          <span className="inline-flex items-center space-x-1 font-mono text-[10px] text-[#F59E0B]">
                            <span>({rec.userId?.userIdTag})</span>
                            <CopyButton text={rec.userId?.userIdTag} />
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1 text-[10px] text-[#9CA3AF] mt-0.5">
                        <span>{rec.userId?.email}</span>
                        {rec.userId?.email && <CopyButton text={rec.userId?.email} />}
                      </div>
                    </td>
                    <td className="py-3.5 px-4 font-mono font-bold text-[#FFFFFF]">
                      <div className="flex items-center space-x-1.5">
                        <span>#{rec.mt5AccountId?.accountNumber || 'MT5'}</span>
                        {rec.mt5AccountId?.accountNumber && <CopyButton text={rec.mt5AccountId?.accountNumber} />}
                      </div>
                    </td>
                    <td className="py-3.5 px-4 font-bold text-[#10B981]">
                      +${rec.closedPnL.toFixed(2)}
                    </td>
                    <td className="py-3.5 px-4 font-semibold text-[#F59E0B]">
                      {rec.profitShareRate}%
                    </td>
                    <td className="py-3.5 px-4 font-bold text-[#F97316]">
                      -${rec.profitShareAmount.toFixed(2)}
                    </td>
                    <td className="py-3.5 px-4 font-bold text-[#FFFFFF]">
                      +${rec.userNetProfit.toFixed(2)}
                    </td>
                    <td className="py-3.5 px-4 text-[#9CA3AF]">
                      {new Date(rec.syncedAt).toLocaleString()}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setSelectedRecordForFlow(rec)}
                        className="flex items-center space-x-1.5 ml-auto text-[11px]"
                      >
                        <Eye className="w-3.5 h-3.5 text-[#F97316]" />
                        <span>Inspect Flow</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* AUDIT HISTORY MODAL */}
      {showAuditModal && (
        <div className="fixed inset-0 z-50 !mt-0 bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-6xl bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-2xl space-y-4 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A] shrink-0">
              <div>
                <h3 className="text-base font-extrabold text-[#FFFFFF] flex items-center space-x-2">
                  <Terminal className="w-5 h-5 text-[#60A5FA]" />
                  <span>Profit Sharing Cycle Audit History & Logs</span>
                </h3>
                <p className="text-xs text-[#9CA3AF] mt-0.5">
                  Real-time audit log files generated automatically whenever profit sharing sync cycles execute.
                </p>
              </div>
              <button onClick={() => setShowAuditModal(false)} className="text-[#9CA3AF] hover:text-white text-sm font-bold p-1">✕</button>
            </div>

            {/* Main Dual-Pane Content */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-1 overflow-hidden min-h-[450px]">
              {/* Left Pane: Cycle Audit List */}
              <div className="lg:col-span-4 bg-[#09090B] border border-[#27272A] rounded-2xl p-3 flex flex-col space-y-2 overflow-y-auto">
                <span className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider px-1">Past Sync Runs</span>
                {loadingAuditLogs ? (
                  <div className="p-4 text-center text-xs text-[#9CA3AF]">Loading audit history...</div>
                ) : auditLogs.length === 0 ? (
                  <div className="p-4 text-center text-xs text-[#9CA3AF]">No audit cycle logs recorded yet.</div>
                ) : (
                  auditLogs.map((log) => {
                    const isSelected = selectedLogDetail && (selectedLogDetail._id === log._id || selectedLogDetail.cycleId === log.cycleId);
                    return (
                      <button
                        key={log._id || log.cycleId}
                        onClick={() => handleSelectLog(log._id || log.cycleId)}
                        className={`w-full text-left p-3 rounded-xl border transition-all space-y-1.5 ${
                          isSelected 
                            ? 'bg-[#1E293B] border-[#3B82F6] text-[#FFFFFF] shadow-md' 
                            : 'bg-[#121215] border-[#27272A] hover:bg-[#1A1A1E] text-[#9CA3AF]'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs font-bold text-[#60A5FA]">{log.cycleId}</span>
                          <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${
                            log.triggerSource === 'manual' ? 'bg-[#F97316]/20 text-[#F97316]' : 'bg-[#10B981]/20 text-[#10B981]'
                          }`}>
                            {log.triggerSource}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-[10px] text-[#9CA3AF]">
                          <span>{new Date(log.startedAt).toLocaleString()}</span>
                          <span className="font-mono font-bold text-[#FFFFFF]">{log.totalAccounts || 0} Accounts</span>
                        </div>

                        <div className="flex items-center justify-between text-[11px] font-bold pt-1 border-t border-[#27272A]/50">
                          <span className="text-[#10B981]">+${(log.totalClosedPnL || 0).toFixed(2)} PnL</span>
                          <span className="text-[#F97316]">-${(log.totalProfitShareDeducted || 0).toFixed(2)} Cut</span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Right Pane: Terminal Log Viewer */}
              <div className="lg:col-span-8 bg-[#050508] border border-[#27272A] rounded-2xl p-4 flex flex-col space-y-3 flex-1 overflow-hidden">
                {/* Header Controls for Terminal */}
                {selectedLogDetail ? (
                  <>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-[#27272A] shrink-0">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-mono text-sm font-extrabold text-[#FFFFFF]">{selectedLogDetail.cycleId}</span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                            selectedLogDetail.status === 'completed' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#F59E0B]/20 text-[#F59E0B]'
                          }`}>
                            {selectedLogDetail.status}
                          </span>
                        </div>
                        <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                          Duration: <span className="font-mono font-bold text-[#FFFFFF]">{selectedLogDetail.executionDurationMs || 0} ms</span>
                          {' | '}
                          Started: {new Date(selectedLogDetail.startedAt).toLocaleString()}
                        </p>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleCopyLogText(selectedLogDetail.logContentText)}
                          className="flex items-center space-x-1 px-3 py-1.5 rounded-xl bg-[#121215] border border-[#27272A] text-xs font-semibold text-[#9CA3AF] hover:text-white hover:border-[#60A5FA] transition-colors"
                        >
                          {logCopied ? <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5 text-[#60A5FA]" />}
                          <span>{logCopied ? 'Copied!' : 'Copy Log'}</span>
                        </button>

                        <button
                          onClick={() => handleDownloadLogFile(selectedLogDetail._id || selectedLogDetail.cycleId, selectedLogDetail.cycleId)}
                          className="flex items-center space-x-1 px-3 py-1.5 rounded-xl bg-[#3B82F6]/15 border border-[#3B82F6]/40 text-xs font-bold text-[#60A5FA] hover:bg-[#3B82F6]/25 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                          <span>Download .log File</span>
                        </button>
                      </div>
                    </div>

                    {/* Search & Filter Bar */}
                    <div className="relative shrink-0">
                      <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={logSearchTerm}
                        onChange={(e) => setLogSearchTerm(e.target.value)}
                        placeholder="Search log output (e.g. Account #, User Name, STEP 4, DEDUCTION)..."
                        className="w-full bg-[#121215] border border-[#27272A] text-[#FFFFFF] text-xs font-mono rounded-xl pl-9 pr-3 py-2 focus:outline-none focus:border-[#60A5FA]"
                      />
                    </div>

                    {/* Dark Code Terminal Console View */}
                    <div className="flex-1 bg-[#020204] border border-[#1F1F24] rounded-xl p-4 font-mono text-[11px] leading-relaxed text-[#A7F3D0] overflow-y-auto overflow-x-auto select-text">
                      {loadingLogDetail ? (
                        <div className="text-center py-12 text-[#9CA3AF]">Loading cycle log output...</div>
                      ) : getFilteredLogLines().length === 0 ? (
                        <div className="text-center py-12 text-[#9CA3AF]">No log lines match "{logSearchTerm}".</div>
                      ) : (
                        getFilteredLogLines().map((line, idx) => {
                          let lineStyle = 'text-[#A7F3D0]'; // Default emerald green terminal text
                          if (line.includes('===') || line.includes('---')) lineStyle = 'text-[#60A5FA] font-bold';
                          else if (line.includes('[STEP') || line.includes('Cycle ID')) lineStyle = 'text-[#F59E0B] font-bold';
                          else if (line.includes('DEDUCTION') || line.includes('PROFIT SPLIT')) lineStyle = 'text-[#F97316] font-bold';
                          else if (line.includes('WARN') || line.includes('FAILED')) lineStyle = 'text-[#EF4444] font-bold';
                          else if (line.includes('SUCCESS') || line.includes('Result')) lineStyle = 'text-[#10B981] font-bold';

                          return (
                            <div key={idx} className={`${lineStyle} whitespace-pre`}>
                              {line}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-xs text-[#9CA3AF]">
                    <Terminal className="w-8 h-8 text-[#9CA3AF]/40 mb-2" />
                    <span>Select an audit cycle from the left list to view step-by-step log output</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Flow Inspection Modal */}
      {selectedRecordForFlow && (
        <div className="fixed inset-0 z-50 !mt-0 bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card space-y-5">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
              <div>
                <h3 className="text-base font-extrabold text-[#FFFFFF] flex items-center space-x-2">
                  <ShieldCheck className="w-5 h-5 text-[#F97316]" />
                  <span>Profit Sharing Distribution Chain Audit</span>
                </h3>
                <p className="text-xs text-[#9CA3AF] mt-0.5">
                  Trader: <span className="font-bold text-[#FFFFFF]">{selectedRecordForFlow.userId?.name || 'User'}</span>
                  {selectedRecordForFlow.userId?.userIdTag && (
                    <span className="font-mono text-[#F59E0B]"> ({selectedRecordForFlow.userId?.userIdTag})</span>
                  )}
                  {' | '}
                  MT5 Account <span className="font-mono font-bold text-[#FFFFFF]">#{selectedRecordForFlow.mt5AccountId?.accountNumber || 'N/A'}</span>
                </p>
              </div>
              <button onClick={() => setSelectedRecordForFlow(null)} className="text-[#9CA3AF] hover:text-white text-sm font-bold p-1">✕</button>
            </div>

            {/* Clear Financial Overview Cards & Status Banner */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div className="bg-[#09090B] p-3 rounded-2xl border border-[#27272A]">
                <span className="text-[10px] text-[#9CA3AF] uppercase font-bold">Closed Trade Profit</span>
                <p className="text-base font-extrabold text-[#10B981] mt-1">+${selectedRecordForFlow.closedPnL.toFixed(2)} USD</p>
                <p className="text-[10px] text-[#9CA3AF] mt-0.5">MT5 account gain</p>
              </div>
              <div className="bg-[#09090B] p-3 rounded-2xl border border-[#27272A]">
                <span className="text-[10px] text-[#9CA3AF] uppercase font-bold">Calculated Profit Share</span>
                <p className="text-base font-extrabold text-[#F97316] mt-1">-${selectedRecordForFlow.profitShareAmount.toFixed(2)} USD ({selectedRecordForFlow.profitShareRate}%)</p>
                <p className="text-[10px] text-[#9CA3AF] mt-0.5">Platform total share</p>
              </div>
              <div className="bg-[#09090B] p-3 rounded-2xl border border-[#27272A]">
                <span className="text-[10px] text-[#9CA3AF] uppercase font-bold">Trader Web Wallet Status</span>
                {selectedRecordForFlow.deductedFromWallet ? (
                  <>
                    <p className="text-sm font-extrabold text-[#10B981] mt-1">🟢 Fully Deducted</p>
                    <p className="text-[10px] text-[#10B981] mt-0.5">Wallet balance processed</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-extrabold text-[#F59E0B] mt-1">🟡 Pending Deposit</p>
                    <p className="text-[10px] text-[#F59E0B] mt-0.5">
                      Wallet: ${(selectedRecordForFlow.traderWalletBalance || 0).toFixed(2)} ($${selectedRecordForFlow.profitShareAmount.toFixed(2)} uncollected)
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Explanation Notice for Pending Wallet Deductions */}
            {!selectedRecordForFlow.deductedFromWallet && (
              <div className="p-3.5 rounded-2xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-xs text-[#F59E0B] space-y-1">
                <p className="font-bold flex items-center space-x-1.5">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>Pending Wallet Deduction Notice</span>
                </p>
                <p className="text-[11px] text-[#9CA3AF] leading-relaxed">
                  Trader currently has insufficient web wallet balance (${(selectedRecordForFlow.traderWalletBalance || 0).toFixed(2)} USD). The <span className="text-[#F59E0B] font-bold">${selectedRecordForFlow.profitShareAmount.toFixed(2)}</span> profit share is queued for automatic deduction and upline commission distribution as soon as this trader deposits USDT.
                </p>
              </div>
            )}

            {/* Step-by-Step Payment Flow Breakdown Chain */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider">Expected / Processed Payment Flow</h4>
                <span className="text-[10px] text-[#10B981] font-mono font-bold">
                  Total Allocation: ${selectedRecordForFlow.profitShareAmount.toFixed(2)} USD
                </span>
              </div>

              <div className="space-y-2 bg-[#09090B] p-4 rounded-2xl border border-[#27272A] max-h-64 overflow-y-auto">
                {/* 1. Master Admin Flat Cut */}
                <div className="flex items-center justify-between p-3 bg-[#121215] rounded-xl border border-[#F97316]/30 text-xs">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-[#F97316]/20 border border-[#F97316]/30 flex items-center justify-center shrink-0">
                      <ShieldCheck className="w-4 h-4 text-[#F97316]" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="font-bold text-[#FFFFFF]">Master Admin Platform Cut</p>
                        {selectedRecordForFlow.deductedFromWallet ? (
                          <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-[#10B981]/20 text-[#10B981]">✓ Credited</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-[#F59E0B]/20 text-[#F59E0B]">⏳ Pending Wallet Deposit</span>
                        )}
                      </div>
                      <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                        Allocated to Master Admin ({selectedRecordForFlow.adminCutRate || 20}% Flat Cut)
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-mono font-extrabold text-[#F97316] text-sm">
                      +${(selectedRecordForFlow.adminCutAmount !== undefined ? selectedRecordForFlow.adminCutAmount : (selectedRecordForFlow.closedPnL * ((selectedRecordForFlow.adminCutRate || 20) / 100))).toFixed(2)} USD
                    </span>
                    <p className="text-[10px] text-[#F97316] font-bold">({selectedRecordForFlow.adminCutRate || 20}%)</p>
                  </div>
                </div>

                {/* 2. Upline Referral Chain */}
                {(selectedRecordForFlow.uplineChain && selectedRecordForFlow.uplineChain.length > 0) || (selectedRecordForFlow.referralEarnings && selectedRecordForFlow.referralEarnings.length > 0) ? (
                  (selectedRecordForFlow.uplineChain && selectedRecordForFlow.uplineChain.length > 0 ? selectedRecordForFlow.uplineChain : selectedRecordForFlow.referralEarnings).map((earning) => (
                    <div key={earning._id || earning.level} className="flex items-center justify-between p-3 bg-[#121215] rounded-xl border border-[#27272A] text-xs">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-[#F59E0B]/20 border border-[#F59E0B]/30 flex items-center justify-center shrink-0 font-bold text-[#F59E0B] text-xs font-mono">
                          L{earning.level}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <p className="font-bold text-[#FFFFFF]">
                              Level {earning.level} Upline: {earning.referrerId?.name || 'Upline User'}
                              {earning.referrerId?.userIdTag && (
                                <span className="font-mono text-[#F59E0B] font-bold text-[11px]"> ({earning.referrerId?.userIdTag})</span>
                              )}
                            </p>
                            {earning.status === 'credited' || selectedRecordForFlow.deductedFromWallet ? (
                              <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-[#10B981]/20 text-[#10B981]">✓ Credited</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-[#F59E0B]/20 text-[#F59E0B]">⏳ Pending Wallet Deposit</span>
                            )}
                          </div>
                          <div className="flex items-center space-x-1 text-[10px] text-[#9CA3AF] mt-0.5">
                            <span>{earning.referrerId?.email}</span>
                            {earning.referrerId?.email && <CopyButton text={earning.referrerId?.email} />}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-mono font-extrabold text-[#10B981] text-sm">+${earning.commissionAmount.toFixed(2)} USD</span>
                        <p className="text-[10px] text-[#F59E0B] font-bold">({earning.commissionRate}%)</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-xs text-[#9CA3AF]">
                    No upline referrers found for this trader (Direct platform user). Full profit share is allocated to Master Admin.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Deduction Modal */}
      {showDeductionModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
              <h3 className="text-base font-extrabold text-[#FFFFFF]">Manual Profit Share Adjustment</h3>
              <button onClick={() => setShowDeductionModal(false)} className="text-[#9CA3AF] hover:text-white text-sm font-bold">✕</button>
            </div>

            <form onSubmit={handleManualDeductionSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">User Object ID</label>
                <input
                  type="text"
                  required
                  value={manualUserId}
                  onChange={(e) => setManualUserId(e.target.value)}
                  placeholder="Paste User MongoDB Object ID"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl p-3 text-xs font-mono focus:outline-none focus:border-[#F97316]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Deduction Amount ($ USD)</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder="e.g. 150"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl p-3 text-xs font-bold focus:outline-none focus:border-[#F97316]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Adjustment Notes</label>
                <textarea
                  value={manualNotes}
                  onChange={(e) => setManualNotes(e.target.value)}
                  placeholder="Reason for manual wallet deduction..."
                  rows={2}
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl p-3 text-xs focus:outline-none focus:border-[#F97316]"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t border-[#27272A]">
                <Button variant="secondary" size="sm" type="button" onClick={() => setShowDeductionModal(false)}>
                  Cancel
                </Button>
                <Button variant="danger" size="sm" type="submit" disabled={processingDeduction}>
                  {processingDeduction ? 'Processing...' : 'Execute Deduction'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitSharingSettings;
